import React, { useEffect, useRef, useState, useMemo } from "react";
import { customersAPI } from "../api/client";
import { toast } from "react-toastify";
import "./CustomersTab.css";
import { mayPerformWithRole } from "../utils/userAccess";
import { useMasters } from "../context/MastersContext";

const COUNTRY_OPTIONS = ["India", "USA", "Canada", "Dubai", "Malaysia", "UK", "Germany"];
const REGION_OPTIONS = ["India", "North America", "UK & Europe", "Middle East & Africa", "Others"];
const NATURE_OPTIONS = ["External", "Internal", "Inter-Company"];

const INITIAL_CUSTOMER = {
  customerName: "",
  customerGroup: "",
  industryVertical: "",
  location: "",
  country: "",
  customerRegion: "",
  billingCustomer: false,
  endCustomer: false,
  internalCustomer: false,
  natureOfCustomer: "",
  notes: "",
};

function getTypeClass(nature) {
  switch (nature) {
    case "External": return "cust-type-external";
    case "Internal": return "cust-type-internal";
    case "Inter-Company": return "cust-type-intercompany";
    default: return "cust-type-default";
  }
}

function getStatusClass(status) {
  switch (status) {
    case "Draft": return "status-draft";
    case "Yet to start": return "status-yet-to-start";
    case "In progress": return "status-in-progress";
    case "Completed": return "status-completed";
    case "Abandoned": return "status-abandoned";
    default: return "";
  }
}

export default function CustomersTab() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const { getList } = useMasters();
  const COUNTRY_OPTIONS = getList("customer_country");
  const REGION_OPTIONS = getList("customer_region");
  const INDUSTRY_VERTICAL_OPTIONS = getList("industry_vertical");
  const canAddCustomer = mayPerformWithRole(user?.role, "customer", "add", "customer.create");
  const canEditCustomer = mayPerformWithRole(user?.role, "customer", "edit", "customer.edit");
  const canDeleteCustomer = mayPerformWithRole(user?.role, "customer", "delete", "customer.delete");
  const [view, setView] = useState("list");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [natureFilter, setNatureFilter] = useState("");
  const [showNatureDropdown, setShowNatureDropdown] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [groupFilter, setGroupFilter] = useState("");
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const filterToolbarRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    const closeExportDropdownOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        exportDropdownRef.current.open = false;
      }
    };
    document.addEventListener("mousedown", closeExportDropdownOutside);
    return () => document.removeEventListener("mousedown", closeExportDropdownOutside);
  }, []);

  const [formData, setFormData] = useState(INITIAL_CUSTOMER);
  const [isEditing, setIsEditing] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [viewCustomer, setViewCustomer] = useState(null);
  const [customerProjects, setCustomerProjects] = useState([]);
  const [customerRateMaster, setCustomerRateMaster] = useState([]);
  const [customerProjectsLoading, setCustomerProjectsLoading] = useState(false);
  const [customerTimeline, setCustomerTimeline] = useState([]);
  const [customerTimelineLoading, setCustomerTimelineLoading] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await customersAPI.getAll();
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load customers:", err);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCustomers(); }, []);

  const filtered = useMemo(() => {
    let list = customers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        (c.customerId || "").toLowerCase().includes(q) ||
        (c.customerName || "").toLowerCase().includes(q) ||
        (c.customerGroup || "").toLowerCase().includes(q) ||
        (c.location || "").toLowerCase().includes(q) ||
        (c.country || "").toLowerCase().includes(q)
      );
    }
    if (natureFilter) {
      list = list.filter((c) => c.natureOfCustomer === natureFilter);
    }
    if (countryFilter) {
      list = list.filter((c) => c.country === countryFilter);
    }
    if (regionFilter) {
      list = list.filter((c) => c.customerRegion === regionFilter);
    }
    if (groupFilter) {
      list = list.filter((c) => c.customerGroup === groupFilter);
    }
    return list;
  }, [customers, search, natureFilter, countryFilter, regionFilter, groupFilter]);

  const unique = (key) => [...new Set(customers.map((c) => (c[key] || "").trim()).filter(Boolean))].sort();
  const countryOptions = unique("country");
  const regionOptions = unique("customerRegion");
  const groupOptions = [...new Set(customers.map((c) => (c.industryVertical || c.industry_vertical || c.customerGroup || "").trim()).filter(Boolean))].sort();
  const hasActiveFilters = Boolean(search || natureFilter || countryFilter || regionFilter || groupFilter);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "natureOfCustomer" && value === "Internal") {
        next.billingCustomer = false;
        next.endCustomer = false;
        next.internalCustomer = true;
      }
      return next;
    });
  };

  const handleCreateNew = () => {
    setFormData(INITIAL_CUSTOMER);
    setIsEditing(false);
    setView("form");
  };

  const handleEdit = (customer) => {
    setFormData({
      ...customer,
      industryVertical: customer.industryVertical || customer.industry_vertical || customer.customerGroup || "",
      customerGroup: customer.industryVertical || customer.industry_vertical || customer.customerGroup || "",
    });
    setIsEditing(true);
    setView("form");
  };

  const handleViewCustomer = async (customer) => {
    setViewCustomer(customer);
    setCustomerProjects([]);
    setCustomerRateMaster([]);
    setCustomerTimeline([]);
    setCustomerProjectsLoading(true);
    setCustomerTimelineLoading(true);
    try {
      const [projRes, timelineRes] = await Promise.allSettled([
        customersAPI.getProjects(customer.customerId || customer.id),
        customersAPI.getTimeline(customer.id),
      ]);
      if (projRes.status === "fulfilled") {
        setCustomerProjects(projRes.value.data?.projects || []);
        setCustomerRateMaster(projRes.value.data?.rateMaster || []);
      } else {
        console.error("Failed to load customer projects:", projRes.reason);
        setCustomerProjects([]);
        setCustomerRateMaster([]);
      }
      if (timelineRes.status === "fulfilled") {
        setCustomerTimeline(Array.isArray(timelineRes.value.data) ? timelineRes.value.data : []);
      } else {
        console.error("Failed to load customer timeline:", timelineRes.reason);
        setCustomerTimeline([]);
      }
    } catch (err) {
      console.error("Failed to load customer details:", err);
      setCustomerProjects([]);
      setCustomerRateMaster([]);
      setCustomerTimeline([]);
    } finally {
      setCustomerProjectsLoading(false);
      setCustomerTimelineLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSearch("");
    setNatureFilter("");
    setCountryFilter("");
    setRegionFilter("");
    setGroupFilter("");
    setShowNatureDropdown(false);
    setShowCountryDropdown(false);
    setShowRegionDropdown(false);
    setShowGroupDropdown(false);
  };

  const closeAllDropdowns = () => {
    setShowNatureDropdown(false);
    setShowCountryDropdown(false);
    setShowRegionDropdown(false);
    setShowGroupDropdown(false);
  };

  // Clicking anywhere outside the filter toolbar closes every open filter
  // dropdown (native <select> filters already close on outside click).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterToolbarRef.current && !filterToolbarRef.current.contains(event.target)) {
        setShowNatureDropdown(false);
        setShowCountryDropdown(false);
        setShowRegionDropdown(false);
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderFilterDropdown = ({ label, allLabel, values, value, onSelect, open, onToggle }) => (
    <div className="filter-dropdown">
      <button
        type="button"
        className={`filter-trigger ${value ? "active" : ""}`}
        onClick={onToggle}
      >
        <span>{label}</span>
        <span className="filter-value">{value || allLabel}</span>
        <span className="filter-arrow">↓</span>
      </button>
      {open && (
        <div className="filter-menu">
          <button
            className={`filter-option ${value === "" ? "selected" : ""}`}
            onClick={() => { onSelect(""); closeAllDropdowns(); }}
          >
            {allLabel}
          </button>
          {values.map((v) => (
            <button
              key={v}
              className={`filter-option ${value === v ? "selected" : ""}`}
              onClick={() => { onSelect(v); closeAllDropdowns(); }}
            >
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    if (payload.natureOfCustomer === "Internal") {
      payload.billingCustomer = false;
      payload.endCustomer = false;
      payload.internalCustomer = true;
    }
    if (!payload.billingCustomer && !payload.endCustomer && !payload.internalCustomer) {
      toast.error("Select at least one Customer Classification (Billing/End/Internal)");
      return;
    }
    setFormLoading(true);
    try {
      if (isEditing) {
        await customersAPI.update(formData.id, payload);
        toast.success("Customer updated successfully");
      } else {
        await customersAPI.create(payload);
        toast.success("Customer created successfully");
      }
      await loadCustomers();
      setView("list");
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err.response?.data?.error || "Failed to save customer");
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!toggleTarget) return;
    const currentlyActive = toggleTarget.isActive !== 0 && toggleTarget.isActive !== false;
    const nextActive = !currentlyActive;
    setToggleLoading(true);
    try {
      await customersAPI.toggle(toggleTarget.id, nextActive ? 1 : 0);
      toast.success(nextActive ? "Customer activated successfully" : "Customer deactivated successfully");
      setToggleTarget(null);
      await loadCustomers();
    } catch (err) {
      console.error("Toggle customer status error:", err);
      toast.error(err.response?.data?.error || "Failed to update customer status");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await customersAPI.exportData(format);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers-export.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export customers error:", err);
      toast.error("Failed to export customers");
    } finally {
      setExporting(false);
    }
  };

  /* ============================================================
     RENDER: LIST VIEW
     ============================================================ */

  if (view === "list") {
    return (
      <div className="cust-tab">

        <div className="cust-header">
          <div>
            <h2>Customers</h2>
            <p>Manage customer master data</p>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <details className="cust-export-dropdown" ref={exportDropdownRef}>
              <summary className="cust-btn-secondary" onClick={(event) => {
                if (exporting || loading || customers.length === 0) event.preventDefault();
              }}>{exporting ? "Exporting..." : "Export ▾"}</summary>
              <div className="cust-export-menu" role="menu">
                <button type="button" role="menuitem" onClick={(event) => { event.currentTarget.closest("details").open = false; handleExport("xlsx"); }}>Excel</button>
                <button type="button" role="menuitem" onClick={(event) => { event.currentTarget.closest("details").open = false; handleExport("csv"); }}>CSV</button>
              </div>
            </details>
            <button type="button" className="cust-btn-primary" onClick={handleCreateNew} disabled={!canAddCustomer}>
              + Create Customer
            </button>
          </div>
        </div>

        <div className="card">
          <div className="search-header">
            <h3>All Customers ({customers.length})</h3>
            <div className="search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name, group, location, or country..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="clear-search-btn" type="button" onClick={() => setSearch("")}>Clear</button>
                )}
              </div>
            </div>
          </div>

          <div className="filter-toolbar" ref={filterToolbarRef}>
            <div className="filter-toolbar-left">
              {renderFilterDropdown({
                label: "Type",
                allLabel: "All Types",
                values: NATURE_OPTIONS,
                value: natureFilter,
                onSelect: (v) => { setNatureFilter(v || ""); closeAllDropdowns(); },
                open: showNatureDropdown,
                onToggle: () => { closeAllDropdowns(); setShowNatureDropdown(!showNatureDropdown); },
              })}
              {renderFilterDropdown({
                label: "Country",
                allLabel: "All Countries",
                values: countryOptions,
                value: countryFilter,
                onSelect: (v) => { setCountryFilter(v || ""); closeAllDropdowns(); },
                open: showCountryDropdown,
                onToggle: () => { closeAllDropdowns(); setShowCountryDropdown(!showCountryDropdown); },
              })}
              {renderFilterDropdown({
                label: "Region",
                allLabel: "All Regions",
                values: regionOptions,
                value: regionFilter,
                onSelect: (v) => { setRegionFilter(v || ""); closeAllDropdowns(); },
                open: showRegionDropdown,
                onToggle: () => { closeAllDropdowns(); setShowRegionDropdown(!showRegionDropdown); },
              })}
              {renderFilterDropdown({
                label: "Industry Vertical",
                allLabel: "All Verticals",
                values: groupOptions,
                value: groupFilter,
                onSelect: (v) => { setGroupFilter(v || ""); closeAllDropdowns(); },
                open: showGroupDropdown,
                onToggle: () => { closeAllDropdowns(); setShowGroupDropdown(!showGroupDropdown); },
              })}
            </div>
            {hasActiveFilters && (
              <button type="button" className="btn outline filter-reset-btn" onClick={clearAllFilters}>
                Clear Filters
              </button>
            )}
          </div>

        <div className="cust-table-wrapper">
          {loading ? (
            <div className="cust-loading">
              <div className="cust-spinner" />
              <p>Loading customers...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="cust-empty">
              <div className="empty-icon">&#128101;</div>
              <h3>No customers found</h3>
              <p>
                {hasActiveFilters
                  ? "Try adjusting your search or filters."
                  : 'Click "+ Create Customer" to get started.'}
              </p>
              {!hasActiveFilters && canAddCustomer && (
                <button type="button" className="cust-btn-primary" onClick={handleCreateNew}>
                  + Create Customer
                </button>
              )}
            </div>
          ) : (
            <table className="cust-table">
              <thead>
                <tr>
                  <th scope="col">Customer ID</th>
                  <th scope="col">Customer Name</th>
                  <th scope="col">Industry Vertical</th>
                  <th scope="col">Location</th>
                  <th scope="col">Country</th>
                  <th scope="col">Region</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span className="cust-id-badge">{c.customerId || "\u2014"}</span>
                    </td>
                    <td>
                      <div className="cust-name-cell">
                        <span className="cust-avatar">
                          {(c.customerName || "?").charAt(0).toUpperCase()}
                        </span>
                        {c.customerName}
                      </div>
                    </td>
                    <td>{c.industryVertical || c.industry_vertical || c.customerGroup || "\u2014"}</td>
                    <td>{c.location || "\u2014"}</td>
                    <td>{c.country || "\u2014"}</td>
                    <td>{c.customerRegion || "\u2014"}</td>
                    <td>
                      <span className={`cust-type-badge ${getTypeClass(c.natureOfCustomer)}`}>
                        {c.natureOfCustomer || "Not set"}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${c.isActive !== 0 && c.isActive !== false ? "status-completed" : "status-abandoned"}`}>
                        {c.isActive !== 0 && c.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="cust-actions-cell">
                      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <button type="button" className="btn outline" onClick={() => handleViewCustomer(c)}>View</button>
                        {canEditCustomer && (
                          <button type="button" className="btn primary" onClick={() => handleEdit(c)}>Edit</button>
                        )}
                        {canEditCustomer && (
                          <button type="button" className={`btn ${c.isActive !== 0 && c.isActive !== false ? "danger" : "primary"}`} onClick={() => setToggleTarget(c)}>
                            {c.isActive !== 0 && c.isActive !== false ? "Inactive" : "Activate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
        </div>

        {toggleTarget && (
          <div className="cust-modal-overlay" onClick={() => setToggleTarget(null)}>
            <div className="cust-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>{toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? "Deactivate Customer" : "Activate Customer"}</h3>
              <p>
                Are you sure you want to {toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? "deactivate" : "activate"} <strong>{toggleTarget.customerName}</strong>?
                {toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? " This customer will be marked as inactive." : " This customer will be marked as active."}
              </p>
              <div className="cust-modal-actions">
                <button type="button" className="cust-btn-secondary" onClick={() => setToggleTarget(null)} disabled={toggleLoading}>Cancel</button>
                <button type="button" className={toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? "cust-btn-danger" : "cust-btn-primary"} onClick={handleToggleActive} disabled={toggleLoading}>
                  {toggleLoading ? (toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? "Deactivating..." : "Activating...") : (toggleTarget.isActive !== 0 && toggleTarget.isActive !== false ? "Deactivate" : "Activate")}
                </button>
              </div>
            </div>
          </div>
        )}

        {viewCustomer && (
          <div className="cust-modal-overlay" onClick={() => setViewCustomer(null)}>
            <div className="cust-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cust-detail-header">
                <div className="cust-detail-identity">
                  <div className="cust-detail-avatar">{(viewCustomer.customerName || "?").charAt(0).toUpperCase()}</div>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: 16, margin: 0, color: "#1F2937" }}>{viewCustomer.customerName || "Customer"}</h3>
                    <div className="cust-detail-sub">
                      <span className="cust-id-badge">{viewCustomer.customerId}</span>
                      <span className={`cust-type-badge ${getTypeClass(viewCustomer.natureOfCustomer)}`}>
                        {viewCustomer.natureOfCustomer || "\u2014"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="cust-detail-header-actions">
                  {canEditCustomer && (
                    <button type="button" className="cust-btn-secondary" onClick={() => { setViewCustomer(null); handleEdit(viewCustomer); }}>Edit</button>
                  )}
                  <button type="button" className="cust-btn-secondary" onClick={() => setViewCustomer(null)}>Close</button>
                </div>
              </div>
              <div className="cust-detail-body">
                <div className="cust-detail-section-title">Overview</div>
                <div className="cust-detail-grid">
                  {[
                    ["Customer ID", viewCustomer.customerId],
                    ["Industry Vertical", viewCustomer.industryVertical || viewCustomer.industry_vertical || viewCustomer.customerGroup],
                    ["Location", viewCustomer.location],
                    ["Country", viewCustomer.country],
                    ["Region", viewCustomer.customerRegion],
                    ["Billing Customer", viewCustomer.billingCustomer ? "Yes" : "No"],
                    ["End Customer", viewCustomer.endCustomer ? "Yes" : "No"],
                    ["Internal", viewCustomer.internalCustomer ? "Yes" : "No"],
                  ].map(([label, value]) => (
                    <div key={label} className="cust-detail-card">
                      <div className="detail-label">{label}</div>
                      <div className="detail-value">{value || "\u2014"}</div>
                    </div>
                  ))}
                </div>
                <div className="cust-detail-notes">
                  <div className="detail-label">Notes / Comments</div>
                  <div className="detail-value">{viewCustomer.notes || "\u2014"}</div>
                </div>

                <div className="cust-detail-section-title">Projects &amp; Resources</div>
                {customerProjectsLoading ? (
                  <div className="cust-empty-inline">Loading projects...</div>
                ) : customerProjects.length === 0 ? (
                  <div className="cust-empty-inline">No projects linked to this customer.</div>
                ) : (
                  <div className="cust-projects-list">
                    {customerProjects.map((p) => (
                      <div key={p.id} className="cust-project-block">
                        <div className="cust-project-head">
                          <div className="cust-project-title">
                            <strong>{p.project_id}</strong>
                            <span>{p.description || p.project_type || "Untitled"}</span>
                          </div>
                          <span className={`status-badge ${getStatusClass(p.project_status)}`}>{p.project_status || "\u2014"}</span>
                        </div>
                        {p.resources && p.resources.length > 0 ? (
                          <table className="cust-detail-table">
                            <thead>
                              <tr>
                                <th scope="col">Resource</th>
                                <th scope="col">Role</th>
                                <th scope="col">Allocation</th>
                                <th scope="col">Billing Rate</th>
                                <th scope="col">Billable</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.resources.map((r) => (
                                <tr key={r.id}>
                                  <td>{r.name || r.employee_id || "\u2014"}</td>
                                  <td>{r.po_role || "\u2014"}</td>
                                  <td>{r.allocation ?? 100}%</td>
                                  <td>{r.billing_rate ? `${r.billing_rate} (${r.billing_rate_type || "Monthly"})` : "\u2014"}</td>
                                  <td>{r.billable ? "Yes" : "No"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div style={{ padding: "10px 14px" }}>
                            <span style={{ fontSize: 13, color: "#94A3B8" }}>No resources for this project.</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="cust-detail-section-title">Rate Master</div>
                {customerRateMaster.length === 0 ? (
                  <div className="cust-empty-inline">No rates configured for this customer.</div>
                ) : (
                  <table className="cust-detail-table">
                    <thead>
                      <tr>
                        <th scope="col">Designation</th>
                        <th scope="col">Model</th>
                        <th scope="col">Type</th>
                        <th scope="col">Rate</th>
                        <th scope="col">Currency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerRateMaster.map((r) => (
                        <tr key={r.id}>
                          <td>{r.designation || "\u2014"}</td>
                          <td>{r.commercial_model || "All"}</td>
                          <td>{r.billing_rate_type || "\u2014"}</td>
                          <td>{Number(r.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>{r.currency || "INR"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div className="cust-detail-section-title">Customer Activity Timeline</div>
                {customerTimelineLoading ? (
                  <div className="cust-empty-inline">Loading timeline...</div>
                ) : customerTimeline.length === 0 ? (
                  <div className="cust-empty-inline">No activity recorded yet.</div>
                ) : (
                  <div className="timeline" style={{ position: "relative", paddingLeft: "28px", display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div style={{ position: "absolute", left: "9px", top: "4px", bottom: "4px", width: "2px", background: "#E5E7EB" }} />
                    {[...customerTimeline]
                      .sort((a, b) => {
                        const ta = new Date(a.eventAt || a.changed_at || 0).getTime();
                        const tb = new Date(b.eventAt || b.changed_at || 0).getTime();
                        return tb - ta;
                      })
                      .map((ev, idx) => {
                        const title = ev.title || ev.action || "Activity";
                        const titleLower = String(title).toLowerCase();
                        const actionLower = String(ev.action || ev.eventType || "").toLowerCase();
                        let dotColor = "#2563EB";
                        if (titleLower.includes("activate") && !titleLower.includes("deactiv")) {
                          dotColor = "#22C55E";
                        } else if (titleLower.includes("deactiv") || actionLower.includes("deactiv")) {
                          dotColor = "#EF4444";
                        } else if (actionLower === "created" || titleLower.includes("create")) {
                          dotColor = "#2563EB";
                        } else if (actionLower === "updated" || titleLower.includes("update")) {
                          dotColor = "#2563EB";
                        }
                        const actorName = ev.actorName || ev.changed_by_name || ev.userName || ev.actor || "System";
                        const actorEmail = ev.actorEmail || ev.changed_by_email || ev.email || "";
                        const ts = ev.eventAt || ev.changed_at || ev.createdAt;
                        const desc = ev.description || ev.details || "";
                        const fieldName = ev.fieldName || ev.field_name || "";
                        const oldValue = ev.oldValue !== undefined ? ev.oldValue : ev.old_value;
                        const newValue = ev.newValue !== undefined ? ev.newValue : ev.new_value;
                        return (
                          <div key={ev.id || idx} style={{ position: "relative", display: "flex", alignItems: "flex-start" }}>
                            <div style={{ position: "absolute", left: "-19px", top: "18px", width: "12px", height: "12px", borderRadius: "50%", background: dotColor, border: "2px solid #fff", boxShadow: "0 0 0 2px #F1F5F9" }} />
                            <div style={{ flex: 1, background: "#fff", border: "1px solid #E5E7EB", borderRadius: "12px", boxShadow: "0 1px 2px rgba(15,23,42,0.02)", overflow: "hidden" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "14px 18px" }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, color: "#111827", fontSize: "14px" }}>{title}</div>
                                  {desc ? <div style={{ color: "#374151", fontSize: "13px", marginTop: "4px", lineHeight: "1.6" }}>{desc}</div> : null}
                                  {(fieldName || (oldValue !== undefined && oldValue !== null && oldValue !== "") || (newValue !== undefined && newValue !== null && newValue !== "")) && (
                                    <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "8px", fontSize: "12px" }}>
                                      {fieldName ? (
                                        <span style={{ background: "#F8FAFC", border: "1px solid #E5E7EB", color: "#475569", borderRadius: "999px", padding: "2px 10px" }}>
                                          <strong style={{ color: "#334155" }}>Changed field:</strong> {fieldName}
                                        </span>
                                      ) : null}
                                      {(oldValue !== undefined && oldValue !== null && oldValue !== "") ? (
                                        <span style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", borderRadius: "999px", padding: "2px 10px" }}>
                                          <strong>Previous:</strong> {String(oldValue)}
                                        </span>
                                      ) : null}
                                      {(newValue !== undefined && newValue !== null && newValue !== "") ? (
                                        <span style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534", borderRadius: "999px", padding: "2px 10px" }}>
                                          <strong>New:</strong> {String(newValue)}
                                        </span>
                                      ) : null}
                                    </div>
                                  )}
                                </div>
                                <div style={{ textAlign: "right", flexShrink: 0 }}>
                                  <div style={{ fontSize: "12px", color: "#475569" }}>{ts ? new Date(ts).toLocaleString() : "-"}</div>
                                  <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
                                    {actorName}
                                    {actorEmail ? <span style={{ color: "#9CA3AF" }}> · {actorEmail}</span> : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ============================================================
     RENDER: CREATE / EDIT FORM
     ============================================================ */

  return (
    <div className="cust-tab">

      <div className="cust-header">
        <div>
          <h2>{isEditing ? `Edit Customer \u2014 ${formData.customerName}` : "Create Customer"}</h2>
          <p>{isEditing ? "Update customer details" : "Fill in the customer details below"}</p>
        </div>
        <button type="button" className="cust-btn-secondary" onClick={() => setView("list")}>&larr; Back to Customers</button>
      </div>

      <form onSubmit={handleFormSubmit}>
        <section className="cust-section">
          <div className="cust-section-header">
            <h3>Customer Details</h3>
          </div>

          <div className="cust-form-grid">
            <div className="cust-form-group">
              <label>Customer ID</label>
              <input
                type="text"
                value={isEditing ? (formData.customerId || "") : "Auto-generated on save"}
                disabled
                placeholder="Auto-generated"
              />
              <span style={{ fontSize: "11px", color: "#94A3B8", marginTop: "4px", display: "block" }}>
                Auto-generated and immutable. Cannot be edited.
              </span>
            </div>
            <div className="cust-form-group">
              <label>Customer Name *</label>
              <input type="text" value={formData.customerName} onChange={(e) => handleChange("customerName", e.target.value)} required placeholder="Enter customer name" />
            </div>
            <div className="cust-form-group">
              <label>Industry Vertical</label>
              {INDUSTRY_VERTICAL_OPTIONS && INDUSTRY_VERTICAL_OPTIONS.length > 0 ? (
                <select value={formData.industryVertical || formData.industry_vertical || formData.customerGroup || ""} onChange={(e) => { handleChange("industryVertical", e.target.value); handleChange("customerGroup", e.target.value); }}>
                  <option value="">Select Industry Vertical</option>
                  {INDUSTRY_VERTICAL_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input type="text" value={formData.industryVertical || formData.industry_vertical || formData.customerGroup || ""} onChange={(e) => { handleChange("industryVertical", e.target.value); handleChange("customerGroup", e.target.value); }} placeholder="e.g. Automotive & Transportation" />
              )}
            </div>
            <div className="cust-form-group">
              <label>Location</label>
              <input type="text" value={formData.location} onChange={(e) => handleChange("location", e.target.value)} placeholder="e.g. Bangalore" />
            </div>
            <div className="cust-form-group">
              <label>Country</label>
              <select value={formData.country} onChange={(e) => handleChange("country", e.target.value)}>
                <option value="">Select Country</option>
                {COUNTRY_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="cust-form-group">
              <label>Customer Region</label>
              <select value={formData.customerRegion} onChange={(e) => handleChange("customerRegion", e.target.value)}>
                <option value="">Select Region</option>
                {REGION_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="cust-form-group">
              <label>Nature of Customer</label>
              <select value={formData.natureOfCustomer} onChange={(e) => handleChange("natureOfCustomer", e.target.value)}>
                <option value="">Select Nature</option>
                {NATURE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="cust-form-group cust-form-group-full">
              <label>Customer Classification <span style={{ color: "#EF4444" }}>*</span></label>
              <div className="cust-checkbox-row">
                <label className="cust-checkbox-label" style={formData.natureOfCustomer === "Internal" ? { opacity: 0.5 } : {}}>
                  <input
                    type="checkbox"
                    checked={formData.billingCustomer}
                    disabled={formData.natureOfCustomer === "Internal"}
                    onChange={(e) => {
                      if (formData.natureOfCustomer === "Internal") return;
                      handleChange("billingCustomer", e.target.checked);
                    }}
                  />
                  Billing Customer
                </label>
                <label className="cust-checkbox-label" style={formData.natureOfCustomer === "Internal" ? { opacity: 0.5 } : {}}>
                  <input
                    type="checkbox"
                    checked={formData.endCustomer}
                    disabled={formData.natureOfCustomer === "Internal"}
                    onChange={(e) => {
                      if (formData.natureOfCustomer === "Internal") return;
                      handleChange("endCustomer", e.target.checked);
                    }}
                  />
                  End Customer
                </label>
                <label className="cust-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.natureOfCustomer === "Internal" ? true : formData.internalCustomer}
                    onChange={(e) => {
                      if (formData.natureOfCustomer === "Internal") {
                        handleChange("internalCustomer", true);
                        return;
                      }
                      handleChange("internalCustomer", e.target.checked);
                    }}
                  />
                  Internal
                </label>
              </div>
              <span style={{ fontSize: "11px", color: "#94A3B8", marginTop: "4px", display: "block" }}>
                {formData.natureOfCustomer === "Internal"
                  ? "When Nature of Customer is Internal, only Internal classification is allowed."
                  : "Select at least one classification."}
              </span>
            </div>
            <div className="cust-form-group cust-form-group-full">
              <label>Notes / Comments</label>
              <textarea maxLength={250} value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder="Any additional notes (max 250 characters)" />
            </div>
          </div>

          <div className="cust-form-actions">
            <button type="button" className="cust-btn-secondary" onClick={() => setView("list")} disabled={formLoading}>Cancel</button>
            <button type="submit" className="cust-btn-primary" disabled={formLoading}>{formLoading ? "Saving..." : isEditing ? "Update Customer" : "Create Customer"}</button>
          </div>
        </section>
      </form>
    </div>
  );
}
